"use client";

import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer2 from "@/components/landing-page/Footer2";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowRight, ArrowLeft, Search, Code2, RefreshCw, Check, ImageIcon, X } from "lucide-react";

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<any | null>(null);
  const [isCallbackPopupOpen, setIsCallbackPopupOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (selectedCaseStudy || isCallbackPopupOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedCaseStudy, isCallbackPopupOpen]);

  const processSteps = [
    {
      title: "Audit",
      timeline: "Week 1-2",
      description: "We map your workflows and find the 5% of opportunities actually worth building. You get a clear roadmap: what to automate, what to build, what to buy off the shelf.",
      icon: <Search className="w-4 h-4 text-black" />
    },
    {
      title: "Build",
      timeline: "Week 2-4",
      description: "We embed within your team and ship AI systems that fit your existing workflows. Custom agents, dashboards, and integrations, built right so they work from day one.",
      icon: <Code2 className="w-4 h-4 text-black" />
    },
    {
      title: "Run",
      timeline: "Ongoing",
      description: "We deploy, monitor, and manage. If something breaks, we fix it. If something can be better, we improve it. You get results, not a handoff.",
      icon: <RefreshCw className="w-4 h-4 text-black" />
    }
  ];

  const caseStudies = [
    {
      client: "TitanTech Investments",
      industry: "Finance",
      title: "Complete Digital Transformation",
      tags: ["Client Work", "live"],
      summary: "How We Rebuilt TitanTech's Legacy Systems to Accelerate Trade Execution and Client Onboarding.",
      heroMetrics: "80% Faster Onboarding, 3x Trade Execution Speed, 100% Data Pipeline Automation.",
      clientDescription: "TitanTech Investments is a mid-sized wealth management firm handling high-net-worth portfolios and complex asset allocations.",
      problem: "TitanTech relied on legacy mainframes and manual data entry across fragmented systems. Client onboarding took up to 3 weeks due to compliance bottlenecks, and trade execution data was heavily delayed, preventing real-time portfolio optimization.",
      solution: "We orchestrated a complete digital transformation. We replaced manual compliance checks with an automated KYC/AML pipeline, integrated modern cloud architecture to handle real-time market data, and developed an AI-driven analytics dashboard for instant portfolio visibility.",
      features: [
        "Automated KYC/AML Onboarding Pipeline",
        "Real-Time Market Data Integration",
        "AI-Driven Portfolio Analytics Dashboard",
        "Secure Cloud Infrastructure Migration"
      ],
      techStack: ["AWS", "Python", "React", "Snowflake", "OpenAI"]
    },
    {
      client: "Luxsun Energy",
      industry: "Infrastructure",
      title: "Infrastructure Automation Excellence",
      tags: ["Client Work", "live"],
      summary: "How We Automated Grid Monitoring and Reduced Infrastructure Downtime by 45%.",
      heroMetrics: "45% Reduction in Downtime, Automated Maintenance Dispatch, Real-time Sensor Telemetry.",
      clientDescription: "Luxsun Energy operates a vast network of solar arrays and power distribution nodes across multiple regions.",
      problem: "Monitoring thousands of distributed energy nodes was done manually via outdated SCADA systems. Anomalies were often detected too late, leading to prolonged grid downtime and inefficient manual dispatching of maintenance crews.",
      solution: "We deployed an IoT sensor integration layer connected to a centralized AI predictive maintenance engine. The system now ingests real-time telemetry, automatically flags impending hardware failures, and dispatches maintenance tickets to the nearest available crew before an outage occurs.",
      features: [
        "IoT Telemetry Data Ingestion",
        "Predictive Maintenance AI Engine",
        "Automated Field Crew Dispatching",
        "Centralized Grid Health Dashboard"
      ],
      techStack: ["Node.js", "PostgreSQL", "TensorFlow", "AWS IoT Core"]
    },
    {
      client: "SVS Food",
      industry: "F&B",
      title: "Full Tech Infrastructure Build",
      tags: ["Client Work", "in-progress"],
      summary: "Full Tech Infrastructure Migration & Build for a Shark Tank Featured Brand",
      heroMetrics: "100% In-House Tech Ownership, Seamless Omnichannel Experience, Custom Mobile & Kiosk Apps.",
      clientDescription: "SVS Food is a fast-growing F&B brand featured on Shark Tank India, expanding aggressively across physical and digital channels.",
      problem: "SVS Food operated on a third-party platform that bundled their website, app, and backend. They had no ownership of the platform, could not deeply customize the digital experience, lacked control over data, and were blocked from scaling their unique operational roadmap.",
      solution: "We are architecting and building their complete in-house technology stack from scratch. This includes a brand-aligned custom website, a consumer-facing mobile app for ordering and loyalty, a self-service kiosk ordering system for physical locations, and a comprehensive backend ERP/CRM system.",
      features: [
        "Custom Brand-Aligned Website",
        "Native Mobile App for Ordering",
        "In-Store Self-Service Kiosk System",
        "Complete Backend ERP & CRM",
        "Omnichannel Cloud Infrastructure"
      ],
      techStack: ["Next.js", "React Native", "Node.js", "PostgreSQL", "AWS"]
    },
    {
      client: "BNB Web",
      industry: "Travel Tech",
      title: "Serverless Cloud Scaling",
      tags: ["Client Work", "live"],
      summary: "How We Migrated to a Serverless Architecture to Handle 10K+ Concurrent Users Flawlessly.",
      heroMetrics: "10K+ Concurrent Users Supported, 99.99% Uptime Achieved, 60% Reduction in Idle Server Costs.",
      clientDescription: "BNB Web is an online booking platform that experiences massive traffic spikes during holiday seasons.",
      problem: "The platform faced severe scalability bottlenecks during peak booking seasons. Their monolithic legacy servers would crash under high load, causing lost bookings and severe customer dissatisfaction.",
      solution: "We orchestrated a complete migration to a serverless architecture. By decoupling their database and implementing auto-scaling microservices, the platform now handles massive traffic spikes automatically without downtime.",
      features: [
        "Serverless Microservices Migration",
        "Auto-Scaling Infrastructure",
        "Database Decoupling & Optimization",
        "High-Availability Load Balancing"
      ],
      techStack: ["AWS Lambda", "DynamoDB", "Next.js", "Vercel"]
    },
    {
      client: "Glinte LipGloss",
      industry: "E-Commerce",
      title: "D2C E-Commerce Growth Engine",
      tags: ["Client Work", "live"],
      summary: "How We Scaled Customer Support 3x and Recovered 25% of Abandoned Carts via Conversational AI.",
      heroMetrics: "25% Cart Recovery Rate, 3x Support Capacity, <30s Avg Response Time.",
      clientDescription: "Glinte LipGloss is a fast-growing D2C beauty brand with a massive social media following and high daily transaction volumes.",
      problem: "Their customer support team was overwhelmed by WISMO (Where Is My Order) inquiries, causing genuine product questions to get lost. Simultaneously, their checkout abandonment rate was soaring, and traditional email retargeting was seeing less than a 2% conversion rate.",
      solution: "We integrated a unified conversational AI engine on WhatsApp and their storefront. The AI handles 80% of tier-1 support queries instantly. We also implemented an automated cart recovery flow via WhatsApp that proactively reaches out to customers, resulting in massive conversion improvements.",
      features: [
        "WhatsApp Conversational AI Integration",
        "Automated WISMO Resolution",
        "Smart Cart Recovery Workflows",
        "Unified Omnichannel Support Dashboard"
      ],
      techStack: ["Shopify API", "WhatsApp Business API", "OpenAI", "Make.com"]
    },
    {
      client: "Fluxx Media",
      industry: "Media & Advertising",
      title: "Full-Stack Media Infrastructure & Content Automation",
      tags: ["Client Work", "live"],
      summary: "How We Built a Scalable Tech Infrastructure and Content Automation Engine to Save Hundreds of Hours on Client Deliverables.",
      heroMetrics: "60% Faster Content Turnaround, 40% Reduction in Ad Operations Overhead, 100% Automated Backend Reporting.",
      clientDescription: "Fluxx Media is a high-growth creative and marketing agency managing large-scale content production and ad campaigns for diverse clients.",
      problem: "Fluxx Media was struggling with fragmented backend systems and manual content workflows. Managing client ad accounts, tracking creative assets, and generating performance reports were all manual processes that bottlenecked their ability to scale and deliver results quickly.",
      solution: "We architected a robust tech infrastructure and a centralized backend management system. We implemented advanced content automation pipelines that handle repetitive creative tasks and integrated automated reporting tools for their ad operations, allowing the team to focus purely on high-level strategy and creativity.",
      features: [
        "Centralized Agency Backend Infrastructure",
        "Automated Creative Content Pipelines",
        "AI-Driven Ad Work Analytics",
        "Dynamic Client Reporting Dashboards"
      ],
      techStack: ["Node.js", "React", "Python", "Google Ads API", "Meta Graph API"]
    },
    {
      client: "Mr. Jain",
      industry: "Real Estate",
      title: "CRM + AI Revenue Automation Engine",
      tags: ["Client Work", "live"],
      summary: "How We Helped Mr. Jain Increase Revenue by 42% Without Hiring a Single New Sales Rep",
      heroMetrics: "42% Revenue Increase, <2 min Lead Response Time, 7.3x Avg Follow-up Touches, 31% Reduction in Site Visit No-shows.",
      clientDescription: "Mr. Jain manages a 6-person real estate sales team handling high-intent property investors and buyers.",
      problem: "Before our engagement, the team was bleeding revenue. Leads arrived from 5+ sources with no unified pipeline, no auto-assignment, and 3-5 hour response times. Follow-ups collapsed to just 2 touches per lead (industry average requires 6-8). Reps wasted time on unqualified browsers instead of high-intent buyers, and management had zero pipeline visibility.",
      solution: "We implemented a 5-Layer Automation Engine. We integrated all lead sources into one CRM with instant auto-assignment and a sub-2-minute WhatsApp/SMS response. We added AI Lead Scoring to prioritize hot leads, automated a 7-step follow-up sequence, introduced self-booking calendar links, and integrated AI call intelligence to detect objections and score deal probabilities.",
      features: [
        "Cross-Platform Lead Capture & Auto-assignment",
        "AI Lead Scoring & Prioritization",
        "Multi-Touch Follow-Up Automation",
        "Automated Site Visit & Calendar Booking",
        "AI Call Intelligence & Sales Analytics"
      ],
      techStack: ["Make.com", "OpenAI", "WhatsApp API", "GoHighLevel", "Twilio"]
    }
  ];

  const faqs = [
    {
      q: "What does Senera Core specialize in?",
      a: "We are an AI development and consultancy firm. We specialize in finding where AI can move the needle in your business, building the systems, and running them for you, whether it's custom software, enterprise automation, or digital transformation."
    },
    {
      q: "How are you different from traditional IT consultancies?",
      a: "We operate as an agile, AI-first product studio. Instead of bloated management layers, you work directly with senior engineers. We focus on shipping prototypes in days and production-ready systems in a matter of weeks."
    },
    {
      q: "What is the typical ROI on an automation project?",
      a: "Most of our clients see a full return on investment within the first 3 to 4 months. By eliminating manual data entry, plugging revenue leakages, and scaling capacity without adding headcount, the financial impact is almost immediate."
    },
    {
      q: "Do we need an existing tech team to use your solutions?",
      a: "Not at all. We build systems designed for business operators, not just engineers. We handle the entire tech stack and provide your team with simple, intuitive dashboards and hands-on training."
    },
    {
      q: "Will AI replace our current staff?",
      a: "No, AI empowers your staff. We automate the repetitive, high-volume tasks (like follow-ups, data entry, and basic support) so your team can focus on high-value human interactions, strategy, and closing deals."
    },
    {
      q: "Which industries do you work with?",
      a: "Our solutions are industry-agnostic. We've successfully implemented AI infrastructure across Finance, Energy, Healthcare, Real Estate, E-Commerce, Logistics, and Professional Services."
    },
    {
      q: "How long does a typical implementation take?",
      a: "Most AI automations or agents take 1–2 weeks. Minimum Viable Products (MVPs) are usually delivered in 4–6 weeks, while more complex enterprise systems take 8–12 weeks. We aim to deliver initial prototypes within 48 hours."
    },
    {
      q: "How secure is our data with your AI systems?",
      a: "Enterprise-grade security is our baseline. We build using private, siloed environments, ensuring your proprietary data is never used to train public AI models. We are fully compliant with standard data protection regulations."
    },
    {
      q: "What if our current software stack is outdated?",
      a: "That's exactly why we're here. We can either build intelligent middleware that connects your legacy systems, or architect a complete migration to a modern, fully-owned tech stack."
    },
    {
      q: "How does the pricing structure work?",
      a: "We don't do bloated hourly billing. We charge flat, transparent project fees based on the value and complexity of the system being built. We also offer optional, low-cost monthly retainers for ongoing maintenance and optimizations."
    },
    {
      q: "Do you offer ongoing management and support?",
      a: "Yes. We don't just hand off the project. We provide flexible support and maintenance to ensure your AI systems evolve and scale alongside your business."
    },
    {
      q: "How do we get started?",
      a: "It begins with a strategy call. We'll audit your workflows, identify high-impact AI use cases, and give you a clear roadmap of what to automate, what to build, and what to buy."
    }
  ];

  return (
    <div className="min-h-screen bg-white text-black selection:bg-indigo-100 relative">
      <Navbar isLoggedIn={false} />

      <main className="pt-24 pb-0 font-sans">
        {/* Hero Section */}
        <section className="px-6 max-w-5xl mx-auto mb-16 pt-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6 max-w-3xl"
          >
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight text-black leading-[1.1]">
              Stop doing things <br className="hidden md:block" />
              <span className="text-black font-bold">the Hard Way.</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-700 max-w-xl leading-relaxed">
              We build AI systems and automation infrastructure for companies ready to streamline workflows, optimize tasks, and reduce manual work using our powerful Task Blocks.
            </p>

            <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-100"
              >
                Book Your Free Strategy Call
                <ArrowRight className="w-4 h-4" />
              </a>
              <button
                onClick={() => setIsCallbackPopupOpen(true)}
                className="inline-flex items-center gap-2 bg-white text-black border border-zinc-200 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-50 transition-colors shadow-sm"
              >
                Get a Callback Now
              </button>
            </div>
          </motion.div>
        </section>

        {/* The Problem — Bento Grid */}
        <section className="px-6 max-w-5xl mx-auto mb-20">
          <div className="mb-10">
            <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-3 block">The Problem</span>
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-black mb-4">
              Your business is quietly bleeding money.
            </h2>
            <p className="text-zinc-700 text-sm md:text-base max-w-2xl leading-relaxed">
              The average company between $1M–$5M in revenue throws away 25–35% of its IT and ops budget every single year. Not from bad calls — from blind spots no one's bothered to uncover.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Card 1 — Large (spans 2 cols) */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
              className="md:col-span-2 bg-zinc-950 border border-zinc-800 rounded-2xl p-6 md:p-8 relative overflow-hidden group"
            >
              <div className="absolute -top-20 -right-20 w-52 h-52 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
              <p className="text-5xl md:text-6xl font-bold text-white tracking-tight mb-4">1 in 3</p>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-lg">
                dollars you spend on SaaS and cloud is pure waste — and most companies have no idea it's happening. That's a third of every invoice going absolutely nowhere.
              </p>
              <span className="inline-block mt-5 text-[10px] font-mono text-indigo-400 tracking-wide uppercase">Flexera 2022 State of ITAM Report</span>
            </motion.div>

            {/* Card 2 — Small */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white border border-zinc-200 rounded-2xl p-6 md:p-8 relative overflow-hidden group hover:border-indigo-200 transition-colors"
            >
              <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none" />
              <p className="text-5xl md:text-6xl font-bold text-indigo-600 tracking-tight mb-4">85%</p>
              <p className="text-zinc-700 text-sm leading-relaxed">
                of companies blow past their cloud budgets, with the average overrun landing at 18% above plan.
              </p>
              <span className="inline-block mt-5 text-[10px] font-mono text-indigo-500 tracking-wide uppercase">Flexera 2025 State of the Cloud</span>
            </motion.div>

            {/* Card 3 — Small */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="bg-white border border-zinc-200 rounded-2xl p-6 md:p-8 relative overflow-hidden group hover:border-indigo-200 transition-colors"
            >
              <p className="text-5xl md:text-6xl font-bold text-black tracking-tight mb-4">~40%</p>
              <p className="text-zinc-700 text-sm leading-relaxed">
                of all software purchased worldwide goes completely unused. Across millions of machines studied, that adds up to over $30 billion wasted annually.
              </p>
              <span className="inline-block mt-5 text-[10px] font-mono text-zinc-500 tracking-wide uppercase">1E — The Real Cost of Unused Software</span>
            </motion.div>

            {/* Card 4 — Large (spans 2 cols) */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="md:col-span-2 bg-zinc-50 border border-zinc-200 rounded-2xl p-6 md:p-8 relative overflow-hidden group"
            >
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <p className="text-5xl md:text-6xl font-bold text-indigo-600 tracking-tight shrink-0">20%</p>
                <div>
                  <p className="text-zinc-800 text-sm md:text-base leading-relaxed">
                    of the technology budget earmarked for new products gets rerouted to fixing tech debt instead. Nearly a third of CIOs say it's even higher — silently eating your capacity to innovate from the inside out.
                  </p>
                  <span className="inline-block mt-4 text-[10px] font-mono text-indigo-500 tracking-wide uppercase">McKinsey Digital — Reclaiming Tech Equity</span>
                </div>
              </div>
            </motion.div>

            {/* Bottom row — Two equal cards */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="bg-white border border-dashed border-zinc-300 rounded-2xl p-6 md:p-8 md:col-span-1"
            >
              <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider mb-3">If your IT spend is $250K/year</p>
              <p className="text-3xl md:text-4xl font-bold text-indigo-600 tracking-tight mb-2">$60–85K</p>
              <p className="text-zinc-600 text-sm">is likely being thrown away every year.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 md:p-8 md:col-span-2"
            >
              <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider mb-3">If your IT spend is $600K/year</p>
              <p className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-violet-400 tracking-tight mb-2">$180–250K</p>
              <p className="text-zinc-500 text-sm">is likely vanishing into thin air, annually.</p>
            </motion.div>
          </div>
        </section>

        {/* Infographic Section — The Efficiency Gap */}
        <section className="px-6 max-w-5xl mx-auto mb-20">
          <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8 md:p-12 overflow-hidden relative">
            <div className="max-w-2xl mb-12 relative z-10">
              <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 mb-3 block">Efficiency Audit</span>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-black mb-4 leading-tight">
                The Gap Between <br className="hidden md:block" />
                Capital and Value.
              </h2>
              <p className="text-zinc-600 text-sm md:text-base leading-relaxed">
                Most companies treat their IT and operations budget as a single block of necessary spend. We see the granular leaks where your capital evaporates before it ever converts into growth.
              </p>
            </div>

            <div className="space-y-12 relative z-10">
              {/* The Visual Waterfall/Flow */}
              <div className="grid md:grid-cols-[1fr_auto_1fr] items-stretch gap-6 md:gap-10">
                {/* Allocated Budget */}
                <div className="bg-white border border-zinc-200 p-6 md:p-8 rounded-xl shadow-sm flex flex-col justify-between">
                  <div>
                    <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2">Budget Allocated</p>
                    <p className="text-4xl font-bold text-black tracking-tighter">100%</p>
                  </div>
                  <div className="mt-6">
                    <div className="w-full h-3 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="w-full h-full bg-black" />
                    </div>
                    <p className="text-[10px] text-zinc-400 mt-2 font-medium">Full operational resources</p>
                  </div>
                </div>

                {/* Transition / Leakage Zone */}
                <div className="flex flex-col items-center justify-center py-4 md:py-0">
                  <div className="h-full w-px bg-zinc-200 hidden md:block relative">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-zinc-50 py-4">
                      <ArrowRight className="w-6 h-6 text-zinc-300" />
                    </div>
                  </div>
                  <div className="md:hidden flex items-center gap-2 text-zinc-300">
                    <ArrowRight className="w-6 h-6 rotate-90" />
                  </div>
                </div>

                {/* Net Realized Value */}
                <div className="bg-zinc-950 text-white p-6 md:p-8 rounded-xl shadow-xl shadow-indigo-100 flex flex-col justify-between relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10">
                    <RefreshCw className="w-12 h-12 animate-spin-slow" />
                  </div>
                  <div className="relative z-10">
                    <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2">Net Realized Value</p>
                    <p className="text-4xl font-bold text-indigo-400 tracking-tighter">~65%</p>
                  </div>
                  <div className="mt-6 relative z-10">
                    <div className="w-full h-3 bg-zinc-800 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        whileInView={{ width: "65%" }}
                        transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                        className="h-full bg-indigo-500" 
                      />
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-2 font-medium">Actual innovation & output</p>
                  </div>
                </div>
              </div>

              {/* Breakdown Labels */}
              <div className="pt-8 border-t border-zinc-200">
                <div className="flex items-center justify-between mb-8">
                  <p className="text-[11px] font-bold text-black uppercase tracking-wider">The 35% Efficiency Gap Breakdown:</p>
                  <div className="px-2 py-0.5 bg-indigo-50 rounded text-[10px] font-bold text-indigo-600 uppercase tracking-tighter">Leakage detected</div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
                  {[
                    { label: "Shadow IT", value: "12%", desc: "Duplicate SaaS subscriptions and unmanaged license sprawl." },
                    { label: "Zombie Assets", value: "8%", desc: "Unused software seats and idle cloud infrastructure costs." },
                    { label: "Manual Drag", value: "10%", desc: "Human time wasted on repetitive data entry and basic ops." },
                    { label: "Tech Debt", value: "5%", desc: "The hidden tax of maintaining legacy, manual-heavy systems." }
                  ].map((item, i) => (
                    <motion.div 
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="group"
                    >
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-sm font-bold text-black group-hover:text-indigo-600 transition-colors">{item.label}</span>
                        <div className="h-px flex-1 bg-zinc-100" />
                        <span className="text-xs font-mono font-bold text-zinc-400">{item.value}</span>
                      </div>
                      <p className="text-[11px] text-zinc-500 leading-relaxed">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Background Decorative Element */}
            <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-indigo-500/5 rounded-full blur-[100px] pointer-events-none" />
          </div>
        </section>

        {/* The Choice Section */}
        <section className="px-6 max-w-5xl mx-auto mb-20">
          <div className="bg-white border border-zinc-200 rounded-2xl p-8 md:p-10 shadow-sm">
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-black mb-8 text-center">
              In 2026, you have two choices for your business:
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Choice 1 */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-6 relative overflow-hidden group hover:border-red-200 transition-colors cursor-default">
                <div className="absolute top-0 left-0 w-1 h-full bg-zinc-300 group-hover:bg-red-400 transition-colors"></div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center border border-zinc-200">
                    <span className="text-zinc-700 font-bold">1</span>
                  </div>
                  <h3 className="font-semibold text-black">The Hard Way</h3>
                </div>
                <p className="text-zinc-700 text-sm leading-relaxed mb-5 font-medium">
                  Continue managing time-consuming, manual processes.
                </p>
                <div className="space-y-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-zinc-900 block mb-1">Let's face it:</span>
                    <div className="flex items-start gap-2">
                      <span className="text-zinc-500 font-bold mt-0.5">↳</span>
                      <p className="text-sm text-zinc-700">Many companies and solopreneurs waste time on repetitive tasks.</p>
                    </div>
                  </div>
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-zinc-900 block mb-1">The reality:</span>
                    <div className="flex items-start gap-2">
                      <span className="text-zinc-500 font-bold mt-0.5">↳</span>
                      <p className="text-sm text-zinc-700">If you're not automating, you're losing time and falling behind.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Choice 2 */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-6 relative overflow-hidden group shadow-md shadow-indigo-100/50 cursor-default">
                <div className="absolute top-0 left-0 w-1 h-full bg-zinc-800 group-hover:bg-zinc-500 transition-colors"></div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-zinc-900 flex items-center justify-center border border-zinc-700">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h3 className="font-semibold text-white">The Smart Way</h3>
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed mb-5 font-medium">
                  Automate tasks and focus on what really matters.
                </p>
                <div className="space-y-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-white block mb-1">That's where Senera Core comes in.</span>
                    <div className="flex items-start gap-2">
                      <span className="text-zinc-600 font-bold mt-0.5">↳</span>
                      <p className="text-sm text-zinc-500">We build AI systems and automation infrastructure for companies ready to stop doing things the hard way.</p>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-start gap-2">
                      <span className="text-zinc-600 font-bold mt-0.5">↳</span>
                      <p className="text-sm text-zinc-500">Our powerful tools, called "Task Blocks," streamline workflows, optimize tasks, and reduce manual work.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section (Audit, Build, Run) */}
        <section id="services" className="px-6 max-w-5xl mx-auto mb-16">
          <div className="mb-10">
            <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-3 block">Services</span>
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-black mb-4">
              We help you make your business AI-native.
            </h2>
            <p className="text-zinc-700 font-mono text-xs md:text-sm max-w-2xl leading-relaxed">
              From figuring out use-cases, to figuring out build v/s buy and building for you. We handle the entire stack so you can focus on running your business.
            </p>
          </div>

          <div className="grid md:grid-cols-3 border border-zinc-200 border-dashed rounded-lg bg-white">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className={`p-5 md:p-6 ${index !== processSteps.length - 1 ? 'border-b md:border-b-0 md:border-r border-zinc-200 border-dashed' : ''}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center border border-zinc-200 bg-zinc-50">
                    {step.icon}
                  </div>
                  <span className="text-[10px] font-mono bg-zinc-100 text-zinc-700 px-2 py-1 rounded">
                    {step.timeline}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-black mb-3">{step.title}</h3>
                <p className="text-zinc-600 font-mono text-xs leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Deliverables Section (AI Agents & Custom Software) */}
        <section className="px-6 max-w-5xl mx-auto mb-16">
          <div className="mb-10">
            <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-3 block">What We Build</span>
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-black mb-4">
              The solutions we deliver.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-5 md:p-6 bg-white border border-zinc-200 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-black mb-3">AI Agents</h3>
              <p className="text-zinc-700 text-sm leading-relaxed">
                Intelligent, autonomous agents that plug directly into your communication channels (WhatsApp, Slack, Telegram, Website). They handle customer support, qualify sales leads, and manage operations 24/7 without human intervention.
              </p>
            </div>
            <div className="p-5 md:p-6 bg-white border border-zinc-200 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-black mb-3">Custom Software</h3>
              <p className="text-zinc-700 text-sm leading-relaxed">
                We take your messy spreadsheets and manual processes and turn them into production-ready software. From custom CRMs to AI-native ERP dashboards, we build the exact internal tools your team needs to scale efficiently.
              </p>
            </div>
          </div>
        </section>

        {/* Case Studies Preview Section */}
        <section id="case-studies" className="px-6 max-w-5xl mx-auto mb-16">
          <div className="mb-10">
            <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-3 block">Work</span>
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-black mb-4">Case Studies</h2>
            <p className="text-zinc-700 max-w-2xl text-sm md:text-base">
              Real businesses, real problems, automated solutions. Click to view details.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {caseStudies.map((study, i) => (
              <div
                key={i}
                onClick={() => setSelectedCaseStudy(study)}
                className="bg-white border border-zinc-200 rounded-lg p-5 md:p-6 hover:border-indigo-200 hover:shadow-md hover:shadow-indigo-100/50 transition-all cursor-pointer group"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="text-[10px] font-mono text-zinc-600 font-semibold">{study.industry}</div>
                  <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-indigo-600 transition-colors" />
                </div>
                <h3 className="text-base font-semibold text-black mb-3">{study.client}</h3>
                <p className="text-zinc-700 text-sm leading-relaxed mb-6 line-clamp-2">{study.summary}</p>

                <div className="pt-4 border-t border-zinc-100">
                  <p className="text-black font-medium text-xs">Outcome: {study.heroMetrics.split(',')[0]}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href="/case-studies"
              className="inline-flex items-center gap-2 bg-white border border-zinc-300 text-black px-6 py-3 rounded-md text-sm font-medium hover:bg-zinc-50 transition-colors shadow-sm"
            >
              View All Case Studies
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-6 max-w-3xl mx-auto mb-16">
          <div className="mb-10">
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-black mb-3">Common Questions</h2>
          </div>

          <div className="border-t border-zinc-200">
            {faqs.map((item, i) => (
              <div key={i} className="border-b border-zinc-200">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex items-center justify-between w-full py-4 text-left hover:text-indigo-600 transition-colors cursor-pointer"
                >
                  <span className="font-medium text-sm text-black pr-8">{item.q}</span>
                  <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pb-5 text-zinc-700 leading-relaxed text-xs md:text-sm">
                        <p dangerouslySetInnerHTML={{ __html: item.a }} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="px-6 max-w-4xl mx-auto mb-16 text-center bg-white border border-zinc-200 rounded-2xl p-6 md:p-8 shadow-sm shadow-zinc-100/50">
          <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-black mb-3">
            Ready to make your business AI-native?
          </h2>
          <p className="text-zinc-700 text-sm md:text-base max-w-2xl mx-auto mb-6">
            Start with a 30-minute strategy call. No pitch, no pressure.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-100"
          >
            Book Your Free Strategy Call
            <ArrowRight className="w-4 h-4" />
          </a>
        </section>

      </main>

      <Footer2 />

      {/* Case Study Modal */}
      <AnimatePresence>
        {selectedCaseStudy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-white overflow-y-auto w-full h-full"
          >
            <div className="max-w-5xl mx-auto px-6 py-8 md:py-12">
              <button
                onClick={() => setSelectedCaseStudy(null)}
                className="text-zinc-600 hover:text-indigo-600 flex items-center gap-2 mb-8 text-xs font-medium uppercase tracking-wider transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              <div className="flex gap-3 mb-6 items-center">
                <span className="bg-zinc-100 text-zinc-700 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider rounded">
                  {selectedCaseStudy.tags[0]}
                </span>
                <span className="text-black text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5 font-bold">
                  <div className="w-1.5 h-1.5 rounded-full bg-zinc-950"></div>
                  {selectedCaseStudy.tags[1]}
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl font-semibold tracking-tight text-black mb-10 leading-[1.1]">
                {selectedCaseStudy.title}
              </h1>

              <div className="border-l-2 border-black pl-4 py-1 mb-12 max-w-4xl">
                <p className="text-base md:text-xl text-zinc-900 leading-relaxed font-medium">
                  {selectedCaseStudy.heroMetrics}
                </p>
              </div>

              <div className="grid md:grid-cols-[1fr_2fr] gap-8 md:gap-16">
                {/* Left Column */}
                <div>
                  <div className="bg-zinc-100 aspect-video rounded-lg mb-8 flex items-center justify-center text-zinc-400">
                    <ImageIcon className="w-12 h-12 opacity-50" />
                  </div>
                  <h3 className="text-lg font-bold text-black mb-3">{selectedCaseStudy.client}</h3>
                  <p className="text-zinc-700 text-sm leading-relaxed">{selectedCaseStudy.clientDescription}</p>
                </div>

                {/* Right Column */}
                <div className="space-y-12">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-4 block">The Problem</span>
                    <p className="text-zinc-800 text-sm leading-relaxed">{selectedCaseStudy.problem}</p>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-4 block">What We Built</span>
                    <p className="text-zinc-800 text-sm leading-relaxed">{selectedCaseStudy.solution}</p>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-4 block">Features</span>
                    <ul className="space-y-3">
                      {selectedCaseStudy.features.map((f: string, i: number) => (
                        <li key={i} className="flex items-start gap-3 text-zinc-800 text-sm">
                          <Check className="w-5 h-5 text-black shrink-0" />
                          <span className="leading-relaxed">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-black mb-4 block">Tech Stack</span>
                    <div className="flex flex-wrap gap-2">
                      {selectedCaseStudy.techStack.map((t: string, i: number) => (
                        <span key={i} className="bg-zinc-100 text-zinc-700 px-3 py-1.5 text-[10px] font-mono rounded">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Close Button */}
              <div className="mt-20 pt-10 border-t border-zinc-200 text-center">
                <button
                  onClick={() => setSelectedCaseStudy(null)}
                  className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors"
                >
                  Close Case Study
                </button>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Callback Popup */}
      <AnimatePresence>
        {isCallbackPopupOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm relative"
            >
              <button
                onClick={() => setIsCallbackPopupOpen(false)}
                className="absolute top-4 right-4 text-zinc-400 hover:text-black transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <h3 className="text-xl font-semibold text-black mb-1">Request a Callback</h3>
              <p className="text-sm text-zinc-500 mb-6">Enter your details and we'll get back to you shortly.</p>
              
              <form onSubmit={async (e) => { 
                e.preventDefault(); 
                setIsSubmitting(true);
                const form = e.target as HTMLFormElement;
                const name = (form.elements.namedItem('name') as HTMLInputElement).value;
                const phone = (form.elements.namedItem('phone') as HTMLInputElement).value;
                
                try {
                  const res = await fetch('/api/callback', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name, phone })
                  });
                  if (res.ok) {
                    setSubmitSuccess(true);
                    setTimeout(() => {
                      setIsCallbackPopupOpen(false);
                      setSubmitSuccess(false);
                    }, 2000);
                  } else {
                    alert('Something went wrong, please try again later.');
                  }
                } catch (error) {
                  alert('Something went wrong, please try again later.');
                } finally {
                  setIsSubmitting(false);
                }
              }} className="space-y-4">
                {submitSuccess ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="text-black font-medium">Request Received!</p>
                    <p className="text-sm text-zinc-500 mt-1">We'll call you shortly.</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-zinc-700 mb-1">Name</label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        required
                        className="w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm bg-white text-black"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-xs font-medium text-zinc-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        required
                        className="w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm bg-white text-black"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-black text-white py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Request"}
                    </button>
                  </>
                )}
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
